import { OVERLAY_ROUTE_NAMES, } from "./OverlayRouteMap";
export const MAX_SEARCH_QUERY_LENGTH = 200;
const MODAL_PREFIX = "modal-";
const STACK_SEPARATOR = "|";
/**
 * Serialize overlay stack entries to a hash string (without leading #).
 *
 * Multiple entries are joined with "|". The search query is encodeURIComponent-encoded
 * so that "|" and "/" in the query never conflict with the separator or product format.
 *
 * Examples:
 *   [cart]                    → "modal-cart"
 *   [product("uuid")]         → "product/uuid"
 *   [search("pizza")]         → "search/pizza"
 *   [search("")]              → "search"
 *   [search("a|b")]           → "search/a%7Cb"
 *   [search("pizza"), product("uuid")] → "search/pizza|product/uuid"
 *   []                        → ""
 */
export function serializeToHash(entries) {
    return entries.map(serializeSegment).join(STACK_SEPARATOR);
}
function serializeSegment(entry) {
    var _a;
    switch (entry.route) {
        case "product":
            return `product/${entry.params.id}`;
        case "search": {
            const q = (_a = entry.params.q) !== null && _a !== void 0 ? _a : "";
            return q ? `search/${encodeURIComponent(q)}` : "search";
        }
        case "modal":
            return `${MODAL_PREFIX}${entry.params.id}`;
        default:
            // All other routes are empty-param; serialize as modal-<routeName>
            return `${MODAL_PREFIX}${entry.route}`;
    }
}
/**
 * Reconstruct overlay stack from a URL hash string (with or without leading #).
 *
 * Handles ALL overlay routes so that browser back/forward navigation fully restores
 * the overlay stack. Cold hydration (page refresh) should call this and then filter
 * to HYDRATABLE_ROUTES at the call site.
 *
 * Examples:
 *   #modal-cart           → { route: "cart" }
 *   #product/<id>         → { route: "product", params: { id } }
 *   #search/<query>       → { route: "search", params: { q: query } }
 */
export function deserializeFromHash(hash) {
    const raw = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!raw)
        return [];
    const segmentMap = buildSegmentMap(raw.split(STACK_SEPARATOR));
    const entries = [];
    for (const route of OVERLAY_ROUTE_NAMES) {
        const rawValue = segmentMap.get(route);
        if (rawValue === undefined)
            continue;
        switch (route) {
            case "product":
                entries.push({ route, params: { id: decodeURIComponent(rawValue) } });
                break;
            case "search":
                entries.push({
                    route,
                    params: {
                        q: decodeURIComponent(rawValue).slice(0, MAX_SEARCH_QUERY_LENGTH),
                    },
                });
                break;
            case "modal":
                entries.push({ route, params: { id: decodeURIComponent(rawValue) } });
                break;
            default:
                entries.push({ route: route, params: {} });
        }
    }
    return entries;
}
/**
 * Convert hash segments into a Map<routeName, rawValue> for lookup by route.
 *
 * Handles the hash-specific encoding:
 *   "product/<x>"  → ("product", x)
 *   "search/<x>"   → ("search",  x)        — x is still percent-encoded
 *   "search"       → ("search",  "")
 *   "modal-<name>" where name is a known empty-param route → (name, "")
 *   "modal-<name>" otherwise                               → ("modal", name)
 */
function buildSegmentMap(segments) {
    const map = new Map();
    for (const segment of segments) {
        if (segment.startsWith("product/")) {
            map.set("product", segment.slice("product/".length));
        }
        else if (segment.startsWith("search/")) {
            map.set("search", segment.slice("search/".length));
        }
        else if (segment === "search") {
            map.set("search", "");
        }
        else if (segment.startsWith(MODAL_PREFIX)) {
            const name = segment.slice(MODAL_PREFIX.length);
            if (!name)
                continue;
            if (OVERLAY_ROUTE_NAMES.has(name) &&
                name !== "modal") {
                map.set(name, "");
            }
            else {
                map.set("modal", name);
            }
        }
    }
    return map;
}
